/* eslint-disable react-refresh/only-export-components */
import Pusher from "pusher-js";
import { useEffect, useState, Suspense } from "react";
import { useDispatch } from "react-redux";
import { Oval } from "react-loader-spinner";
import {
  LoaderFunction,
  LoaderFunctionArgs,
  Outlet,
  redirect,
  useNavigate,
} from "react-router-dom";
import { EcosystemDock } from "~/components/shared/ecosystem-dock";
import VeilighDarkLogo from "~/assets/logos/veiligh_new_logo_dark.png";
import AddActions from "~/components/shared/add-actions";
import Header from "~/components/shared/header";
import MobileFooterTab from "~/components/shared/mobile-tab";
import Notifications from "~/components/shared/notifications";
import SessionTimeout from "~/components/shared/session-timeout";
import OnboardingModal from "~/components/shared/onboarding-modal";
import { AppSidebar } from "~/components/shared/sidebar";
import UserNav from "~/components/shared/user-nav";
import {
  AnimatedSearch,
  Button,
  Icon, // AnimatedSearch,
} from "~/components/ui";
import SearchWithAnimation from "~/components/ui/search";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "~/components/ui/sidebar";
import {
  DEFAULT_API_PAYLOAD, //  DEFAULT_API_PAYLOAD,
  routes,
} from "~/lib/constants";
import { getPusher } from "~/lib/pusher";
// Changed import
import { waitForTimeout } from "~/lib/utils";
import { notificationsApi } from "~/redux/api/notifications";
import { restApi } from "~/redux/restApi";
import { loadSessionStore } from "~/redux/session";
import { commonActions } from "~/redux/slice/common";
import { modelActions } from "~/redux/slice/models";
import { useSelector } from "~/redux/store";
import { ActionHandlerProvider } from "~/components/shared/action-handler/context";

import "../../styles/sidenav.css";
import { INotification } from "./type";
import { authActions } from "~/redux/slice/auth";
import { Permission } from "~/lib/utils";

/**
 * Refreshes the current user's permissions from the API and syncs them
 * into the Redux store. This ensures pages that rely on
 * `state.persistedReducer.auth.permissions` stay in sync with changes
 * made by admins (e.g. toggling a permission in the access panel)
 * without requiring the user to log out and back in.
 */
function usePermissionRefresh() {
  const dispatch = useDispatch();
  const [fetchPermissions] = restApi.useLazyGetPermissionByUserQuery();

  useEffect(() => {
    const refresh = async () => {
      try {
        const response = await fetchPermissions({
          ...DEFAULT_API_PAYLOAD,
        }).unwrap();
        const permissions = (response?.result ?? []) as Permission[];
        if (permissions.length > 0) {
          dispatch(authActions.setUserPermissions(permissions));
        }
      } catch {
        // Non-blocking: if the refresh fails, the user keeps their cached permissions
      }
    };
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

function ContentLoader() {
  return (
    <div className="flex h-full min-h-[400px] w-full flex-col items-center justify-center rounded-xl bg-neutral-100">
      <Oval
        height="80"
        width="80"
        strokeWidthSecondary={4}
        color="#CBFF10"
        secondaryColor="#D9D9D9"
        ariaLabel="oval-loading"
        strokeWidth={4}
      />
    </div>
  );
}

export default function PortalLayout() {
  // Refresh permissions from API on layout mount — keeps Redux in sync
  usePermissionRefresh();

  const HeaderActionsStart = () => {
    const navigate = useNavigate();
    const { state, isMobile } = useSidebar();
    const dispatch = useDispatch();
    const userId = useSelector((state) => state.persistedReducer.auth.userId);
    const detailedPage = useSelector((state) => state.common.detailedPage);
    const userRoles = useSelector(
      (state) => state.persistedReducer.auth.userTokenInfo?.roles,
    );
    const [showSearch, setShowSearch] = useState(false);
    const [getProfile, { data }] = restApi.useLazyProfileQuery();
    const profile = data?.result;

    useEffect(() => {
      if (userId) {
        getProfile({
          ...DEFAULT_API_PAYLOAD,
          id: userId,
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    useEffect(() => {
      if (profile?.firstName) {
        waitForTimeout(5000).then(() => setShowSearch(true));
      }
    }, [profile?.firstName]);

    useEffect(() => {
      if (!userId) return;

      let pusher: Pusher;
      let channel: any;

      const initializePusher = async () => {
        try {
          pusher = await getPusher();
          channel = pusher.subscribe(userId);

          channel.bind("new-notification", (data: INotification) => {
            dispatch(modelActions.addNotification(data));
            // Server is the source of truth: refetch the persisted list + unread count so the
            // API-backed notification bell reflects the realtime event.
            dispatch(notificationsApi.util.invalidateTags(["Notifications" as any]));
          });
        } catch (error) {
          console.error("Failed to initialize Pusher:", error);
        }
      };

      initializePusher();

      return () => {
        // Cleanup: unsubscribe from the channel, not the pusher instance
        if (channel) {
          pusher?.unsubscribe(userId);
        }
      };
    }, [userId, dispatch]);

    return (
      <div>
        <div className="hidden w-full items-center gap-4 px-4 sm:flex">
          {(state === "collapsed" || isMobile) && (
            <SidebarTrigger className="-ml-1" />
          )}
          {showSearch ? (
            <AnimatedSearch />
          ) : (
            <div>
              <p>Welcome Back, {profile?.firstName}!</p>
            </div>
          )}
        </div>
        <div className="sm:hidden">
          {detailedPage ? (
            <div className="relative flex w-full items-center">
              <Button
                type="button"
                variant="outline"
                className="z-10"
                onClick={() => {
                  dispatch(commonActions.toggleDetailedPage(null));
                  navigate(-1);
                }}
              >
                <Icon name="ArrowLeft" />
              </Button>

              <div className="absolute left-2/3 top-1/2 flex w-full -translate-x-1/2 -translate-y-1/2 items-center justify-center gap-2">
                <span>{detailedPage.title}</span>
              </div>
            </div>
          ) : (
            <div className="flex items-center">
              <span className="inline-block h-auto w-40 shrink-0 p-5 py-2 text-center">
                <img src={VeilighDarkLogo} alt="Logo" />
              </span>
              {!isMobile && (
                <span className="text-preview-sm-2">
                  {userRoles?.map((role) => role.displayName).join(", ")}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const MobileHeaderActionsStart = ({
    setMobileSearchOpen,
    mobileSearchOpen,
  }: {
    setMobileSearchOpen: React.Dispatch<React.SetStateAction<boolean>>;
    mobileSearchOpen: boolean;
  }) => {
    const { state, isMobile } = useSidebar();
    const detailedPage = useSelector((state) => state.common.detailedPage);

    return (
      <div className="flex w-full items-center gap-4 px-4">
        <SearchWithAnimation
          isOpen={mobileSearchOpen}
          setIsOpen={setMobileSearchOpen}
        />
        {(state === "collapsed" || isMobile) && (
          <div>
            {detailedPage?.action ? (
              detailedPage?.action
            ) : (
              <SidebarTrigger variant={"outline"} />
            )}
          </div>
        )}
      </div>
    );
  };

  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset className="h-screen bg-neutral-100">
        <Header
          headerStart={mobileSearchOpen ? null : <HeaderActionsStart />}
          headerEnd={
            <div>
              <div className="hidden items-center gap-12 sm:flex">
                <AddActions />
                <div className="flex items-center gap-4">
                  <Notifications />
                  <UserNav />
                </div>
              </div>
              <div className="flex sm:hidden">
                <MobileHeaderActionsStart
                  setMobileSearchOpen={setMobileSearchOpen}
                  mobileSearchOpen={mobileSearchOpen}
                />
              </div>
            </div>
          }
        />
        <div className="flex flex-1 flex-col gap-4 overflow-hidden px-4 pb-0 pt-0">
          <div className="min-h-0 flex-1 rounded-xl bg-neutral-100 py-5">
            <Suspense fallback={<ContentLoader />}>
              <ActionHandlerProvider>
                <Outlet />
              </ActionHandlerProvider>
            </Suspense>
          </div>
        </div>
        <MobileFooterTab />
        <SessionTimeout />
        <OnboardingModal />
        <EcosystemDock />
      </SidebarInset>
    </SidebarProvider>
  );
}

export const protectedLoader: LoaderFunction = async ({
  request,
}: LoaderFunctionArgs) => {
  // If the user is not logged in and tries to access `/protected`, we redirect
  // them to `/login` with a `from` parameter that allows login to redirect back
  // to this page upon successful authentication
  const params = new URLSearchParams();

  const auth = await loadSessionStore();
  const userId = auth?.userId;

  if (!userId) {
    params.set("redirectTo", new URL(request.url).pathname);

    return redirect(`${routes.login()}?` + params.toString());
  }

  if (new URL(request.url).pathname === "/") {
    return redirect(`${routes.home()}`);
  }

  return null;
};
